import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  passwordConfirmationValidator,
  passwordValidator,
} from '../../utils/validators/validators';
import { ActivatedRoute } from '@angular/router';
import {
  ImmomioIconName,
  IPasswordResetFormValues,
  IResetPasswordBeanPayload,
} from '../../../shared/models';

/* the password reset can either be unauthenticaed via reset link or authenticated from inside the app.
The component ca detect if it is a reset link if there is a :token in the params */

@Directive()
export abstract class PasswordResetFormBaseComponent implements OnInit, OnDestroy {
  @Input() isResetLink = true;

  @Output() formSubmit = new EventEmitter();

  public immomioIconName = ImmomioIconName;

  fg: FormGroup;
  private subscription: Subscription;

  protected constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.fg = this.fb.group({
      oldPassword: ['', [Validators.required]],
      password: ['', [Validators.required, passwordValidator]],
      passwordConfirm: ['', [Validators.required, passwordConfirmationValidator]],
    });

    const token = this.route.snapshot.params['token'];

    if (token) {
      this.isResetLink = true;
      this.fg.addControl('token', this.fb.control(token, [Validators.required]));
      this.fg.removeControl('oldPassword');
    }
    // Re-validate passwordConfirm whenever password changes
    this.subscription = this.fg.get('password')?.valueChanges.subscribe(() => {
      const passwordConfirm = this.fg.get('passwordConfirm');
      if (passwordConfirm?.value) {
        passwordConfirm.updateValueAndValidity();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public validatePassword(focus: boolean): void {
    this.fg.get('password')?.markAsTouched();
  }

  submit() {
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      return;
    }

    const payload = this.formatPayload();
    this.formSubmit.next(payload);
  }

  private formatPayload() {
    const values = this.fg.value;
    if (this.isResetLink) {
      return {
        token: values?.token,
        password: values.password,
        confirmedPassword: values.passwordConfirm,
      } as IResetPasswordBeanPayload;
    } else {
      return {
        oldPassword: values.oldPassword,
        newPassword: values.password,
        confirmedNewPassword: values.passwordConfirm,
      } as IPasswordResetFormValues;
    }
  }
}
